"use client";

import { Check, CircleXIcon, LoaderCircleIcon, XIcon } from "lucide-react";
import * as React from "react";
import { useDebouncedCallback } from "use-debounce";

import { Button } from "@/app/_components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/app/_components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_components/ui/popover";
import { cn } from "@/lib/utils";
import clsx from "clsx";
import { useInView } from "react-intersection-observer";

export type ComboBoxItemType = {
  value: string;
  label: string;
  description?: string | React.ReactNode;
  /**
   * Optional section heading. Consecutive items sharing a group render under
   * one heading; items without a group render headerless as before.
   */
  group?: string;
};

type ComboboxProps = {
  value?: string;
  onSelect: (value?: string) => void;
  items: ComboBoxItemType[];
  searchPlaceholder?: string;
  noResultsMsg?: string | React.ReactNode;
  selectItemMsg?: string | React.ReactNode;
  className?: string;
  unselect?: boolean;
  unselectMsg?: string;
  onSearchChange?: (e: string) => Promise<void>;
  multiple?: boolean;
  selectedItems?: ComboBoxItemType[];
  onRemoveItem?: (value: string) => void;
  horizontal?: boolean;
  autoOpen?: boolean;
  loading?: boolean;
  footerElement?: React.ReactNode;
  // For infinite scrolling
  onScrollBottom?: (search: string) => Promise<void>;
  noMoreResults?: boolean;
  buttonVariant?:
    | "default"
    | "outline"
    | "secondary"
    | "link"
    | "destructive"
    | "ghost";
  buttonSize?: "default" | "xs" | "sm" | "lg" | "icon" | "inline" | null;
  muteSelectItemText?: boolean;
  customTrigger?: React.ReactNode;
  onRemoveSelectedClick?: () => void;
  popoverContentClassName?: string;
  /** Preferred popover side. Defaults to Radix's `bottom`. */
  popoverSide?: "top" | "right" | "bottom" | "left";
  /**
   * When false, the list stays on `popoverSide` even if it overflows the
   * viewport (instead of flipping above the trigger). Defaults to true.
   */
  avoidCollisions?: boolean;
  selectedValueOverride?: string;
  selectedItemsWrapperClassName?: string;
  selectedItemClassName?: string;
  disabled?: boolean;
};

const popOverStyles = {
  width: "var(--radix-popover-trigger-width)",
};

export function Combobox({
  value,
  onSelect,
  items,
  searchPlaceholder = "Search item...",
  noResultsMsg = "No items found",
  selectItemMsg = "Select an item",
  className,
  onSearchChange,
  multiple,
  selectedItems,
  onRemoveItem,
  horizontal,
  autoOpen,
  loading,
  footerElement,
  onScrollBottom,
  noMoreResults,
  buttonVariant = "outline",
  buttonSize = "default",
  muteSelectItemText,
  customTrigger,
  onRemoveSelectedClick,
  popoverContentClassName,
  popoverSide = "bottom",
  avoidCollisions = true,
  selectedValueOverride,
  selectedItemsWrapperClassName,
  selectedItemClassName,
  disabled = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(autoOpen ?? false);
  const [searchValue, setSearchValue] = React.useState("");
  const [typing, setTyping] = React.useState(false);

  // Infinite scroll config
  const { ref: infiniteScrollRef, inView: infiniteScrollInView } = useInView();
  const [isFetching, setIsFetching] = React.useState(false); // Track loading state
  // Load-more sets `isFetching` too. Keep it separate so a next page does
  // not unmount the current results.
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const pendingFetchCount = React.useRef(0); // Track in-progress requests

  const handleOnSearchChange = useDebouncedCallback((e: string) => {
    setSearchValue(e);

    if (onSearchChange) {
      pendingFetchCount.current += 1; // Increment pending count
      setIsFetching(true); // Set fetching as soon as a request starts

      onSearchChange(e).finally(() => {
        pendingFetchCount.current -= 1; // Decrement pending count
        if (pendingFetchCount.current === 0) {
          setIsFetching(false); // Only set false when no pending requests
        }
        setTyping(false);
      });
    }
  }, 300);

  const handleFetchMore = useDebouncedCallback(async () => {
    if (onScrollBottom && !noMoreResults) {
      pendingFetchCount.current += 1; // Increment pending count
      setIsFetching(true);
      setIsFetchingMore(true);

      try {
        await onScrollBottom(searchValue);
      } finally {
        pendingFetchCount.current -= 1; // Decrement pending count
        if (pendingFetchCount.current === 0) {
          setIsFetching(false); // Only set false when no pending requests
        }
        setIsFetchingMore(false);
      }
    }
  }, 300);

  React.useEffect(() => {
    if (infiniteScrollInView && onScrollBottom && !isFetching) {
      handleFetchMore();
    }
  }, [infiniteScrollInView]);

  // Only hide rows for a typed search in flight — not for infinite-scroll
  // fetches (internal or via the caller's `loading` / isFetchingNextPage),
  // which would unmount the current page, reset scroll, and retrigger the
  // sentinel.
  const hideStaleSearchResults =
    !!onSearchChange && (typing || (isFetching && !isFetchingMore));

  return (
    <>
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          {customTrigger ? (
            customTrigger
          ) : (
            <Button
              type="button"
              variant={buttonVariant}
              size={buttonSize}
              disabled={disabled}
              role="combobox"
              aria-expanded={open}
              className={cn(
                "justify-between whitespace-nowrap w-full focus:outline-none focus:ring-2 focus:ring-ring",
                className,
                {
                  "text-muted-foreground": !value && muteSelectItemText,
                },
              )}
            >
              {value
                ? selectedValueOverride ||
                  items.find((item) => item.value === value)?.label
                : selectItemMsg}
              {onRemoveSelectedClick && value && (
                <XIcon
                  size={16}
                  className="ml-2"
                  onClick={(_) => {
                    onRemoveSelectedClick();
                  }}
                />
              )}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          style={popOverStyles}
          side={popoverSide}
          avoidCollisions={avoidCollisions}
          className={cn(
            "p-0 popover-content-width-same-as-its-trigger",
            popoverContentClassName,
          )}
        >
          <Command shouldFilter={!onSearchChange}>
            <CommandInput
              placeholder={searchPlaceholder}
              onValueChange={(e) => {
                if (onSearchChange) {
                  setTyping(true);
                }
                handleOnSearchChange(e);
              }}
            />
            <CommandEmpty className="py-3 text-center">
              {hideStaleSearchResults ||
              loading ||
              (onScrollBottom && !noMoreResults) ? (
                <div className="flex justify-center py-3">
                  <LoaderCircleIcon
                    size={24}
                    className="text-primary animate-spin"
                  />
                </div>
              ) : (
                <span className="text-sm">{noResultsMsg}</span>
              )}
            </CommandEmpty>
            {/*
              Single scrollport: CommandList owns max-height. Nesting a
              ScrollArea (200px) around CommandList (default 300px) clipped
              later items and the infinite-scroll sentinel.
            */}
            <CommandList className="max-h-[200px]">
              {/*
                Hide stale results while a typed search is in flight so
                CommandEmpty (and its spinner) can render. Keep CommandList
                mounted — unmounting it on keystroke crashes cmdk. Do not hide
                during infinite-scroll fetches or scroll position is lost.
              */}
              {!hideStaleSearchResults &&
                _groupItems(
                  items.filter((item) => {
                    if (multiple) {
                      return !selectedItems?.find(
                        (selected) => selected.value === item.value,
                      );
                    }
                    return value !== item.value;
                  }),
                ).map((group, groupIndex) => (
                  <CommandGroup
                    key={group.heading ?? `group-${groupIndex}`}
                    heading={group.heading}
                  >
                    {group.items.map((item) => (
                      <CommandItem
                        className="cursor-pointer pl-0"
                        key={item.value}
                        value={`${item.label} :: ${item.value}`}
                        onSelect={(currentValue) => {
                          const value = currentValue?.split(" :: ")[1];
                          onSelect(value);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            value === item.value ? "opacity-100" : "opacity-0",
                          )}
                        />
                        <div className="w-full">
                          <div>{item.label}</div>
                          {item.description && (
                            <div className="text-xs">{item.description}</div>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              {onScrollBottom &&
                !noMoreResults &&
                !typing &&
                !isFetching &&
                !loading && <div ref={infiniteScrollRef} />}
            </CommandList>
          </Command>
          {footerElement}
        </PopoverContent>
      </Popover>
      {multiple && selectedItems?.length ? (
        <div
          className={cn(
            "flex flex-row pt-2 flex-wrap cursor-pointer gap-y-2",
            selectedItemsWrapperClassName,
          )}
        >
          {selectedItems?.map((item) => (
            <div
              key={`selected-${item.value}`}
              className={clsx(
                "flex flex-row justify-between items-center rounded-md bg-white border border-primary p-2 w-full lg:w-fit text-primary",
                { "mr-0 lg:mr-2": horizontal, "w-full": !horizontal },
                selectedItemClassName,
              )}
              onClick={() => onRemoveItem && onRemoveItem(item.value)}
            >
              <p>{item.label}</p>
              <CircleXIcon
                size={14}
                strokeWidth={3}
                className="ml-2 shrink-0"
              />
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}

/**
 * Split items into consecutive runs sharing the same `group`, preserving
 * order. Items without a group render in a headerless CommandGroup, matching
 * the previous single-group behavior.
 */
function _groupItems(items: ComboBoxItemType[]) {
  const groups: { heading?: string; items: ComboBoxItemType[] }[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.heading === item.group) {
      last.items.push(item);
    } else {
      groups.push({ heading: item.group, items: [item] });
    }
  }
  return groups;
}
